import React, { useMemo } from 'react';
import { CheckCircle, Circle, Award } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

/**
 * ProfileCompletionBar — Shows profile completion % in sidebar.
 * Checks: name, DOB, phone, profile pic, first conversation, first badge.
 * Awards hearts on 100% completion.
 */
const PROFILE_ITEMS = [
  { key: 'name', label: 'Set your name', check: (u) => !!u?.name },
  { key: 'dob', label: 'Add date of birth', check: (u) => !!u?.dob },
  { key: 'phone', label: 'Add phone number', check: (u) => !!u?.phone },
  { key: 'avatar', label: 'Upload profile photo', check: (u) => !!u?.profile_picture },
  { key: 'onboarding', label: 'Complete onboarding', check: (u) => !!u?.onboarding_complete },
  { key: 'focus', label: 'Choose focus area', check: (u) => !!u?.focus_area },
];

const ProfileCompletionBar = ({ compact = false }) => {
  const { user } = useAuth();

  const { completed, total, percentage, items } = useMemo(() => {
    const items = PROFILE_ITEMS.map(item => ({
      ...item,
      done: item.check(user),
    }));
    const completed = items.filter(i => i.done).length;
    const total = items.length;
    return { completed, total, percentage: Math.round((completed / total) * 100), items };
  }, [user]);

  if (percentage === 100 && localStorage.getItem('hibff_profile_100_dismissed')) return null;

  const barColor = percentage === 100 ? 'bg-emerald-500' : percentage >= 60 ? 'bg-orange-500' : 'bg-purple-500';

  if (compact) {
    return (
      <div className="px-3 py-2" data-testid="profile-completion-compact">
        <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
          <span>Profile</span>
          <span className="font-bold">{percentage}%</span>
        </div>
        <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
          <div className={`h-full rounded-full transition-all duration-500 ${barColor}`} style={{ width: `${percentage}%` }} />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4" data-testid="profile-completion-bar">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold text-gray-800">Profile Completion</h3>
        <span className={`text-sm font-bold ${percentage === 100 ? 'text-emerald-600' : 'text-purple-600'}`}>{percentage}%</span>
      </div>

      {/* Progress bar */}
      <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden mb-3">
        <div className={`h-full rounded-full transition-all duration-700 ease-out ${barColor}`} style={{ width: `${percentage}%` }} />
      </div>

      {/* Checklist */}
      <div className="space-y-2">
        {items.map(item => (
          <div key={item.key} className="flex items-center gap-2">
            {item.done ? (
              <CheckCircle size={14} className="text-emerald-500 flex-shrink-0" />
            ) : (
              <Circle size={14} className="text-gray-300 flex-shrink-0" />
            )}
            <span className={`text-xs ${item.done ? 'text-gray-400 line-through' : 'text-gray-600'}`}>{item.label}</span>
          </div>
        ))}
      </div>

      {/* 100% completion reward */}
      {percentage === 100 && (
        <div className="mt-3 p-2 bg-emerald-50 rounded-lg flex items-center gap-2">
          <Award size={16} className="text-emerald-600" />
          <span className="text-xs font-semibold text-emerald-700">Profile complete! +50 Hearts earned</span>
        </div>
      )}
    </div>
  );
};

export default ProfileCompletionBar;
